/**
 * adminDashboardSlice — Redux state for the admin dashboard KPI + recent orders (CON-ADMIN-002).
 *
 * Two independent data keys (stats, recentOrders) with separate status/error
 * so each section can fail independently. Thunks are idempotent — they bail
 * early when their status is already `'loading'`.
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { getDashboardStats, getRecentOrders } from '@dfq/shared/api/admin/dashboard';
import { ApiError } from '@dfq/shared/api/_envelope';
import type { DashboardStatsDTO, OrderSummaryDTO } from '@dfq/shared/types/dashboard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface AdminDashboardState {
  stats: DashboardStatsDTO | null;
  recentOrders: OrderSummaryDTO[];
  statsStatus: AsyncStatus;
  ordersStatus: AsyncStatus;
  statsError: string | null;
  ordersError: string | null;
  lastRefreshedAt: string | null;
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Fetch KPI stats, orders-by-status chart data, and low-stock alerts.
 * Idempotent — skips if status is already `'loading'`.
 */
export const fetchDashboardStats = createAsyncThunk<
  DashboardStatsDTO,
  void,
  { rejectValue: string; state: { adminDashboard: AdminDashboardState } }
>(
  'adminDashboard/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      return await getDashboardStats();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load dashboard stats';
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { statsStatus } = getState().adminDashboard;
      return statsStatus !== 'loading';
    },
  },
);

/**
 * Fetch the ten most recent orders for the dashboard table.
 * Idempotent — skips if status is already `'loading'`.
 */
export const fetchRecentOrders = createAsyncThunk<
  OrderSummaryDTO[],
  void,
  { rejectValue: string; state: { adminDashboard: AdminDashboardState } }
>(
  'adminDashboard/fetchRecentOrders',
  async (_, { rejectWithValue }) => {
    try {
      const result = await getRecentOrders({ limit: 10, sort: 'recent' });
      return result.orders;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load recent orders';
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { ordersStatus } = getState().adminDashboard;
      return ordersStatus !== 'loading';
    },
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const initialState: AdminDashboardState = {
  stats: null,
  recentOrders: [],
  statsStatus: 'idle',
  ordersStatus: 'idle',
  statsError: null,
  ordersError: null,
  lastRefreshedAt: null,
};

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',
  initialState,
  reducers: {
    /** Reset to idle so the next dispatch re-fetches fresh data. */
    resetDashboard: () => initialState,
    /** Stamp the last successful full refresh timestamp (ISO string). */
    setLastRefreshedAt: (state, action: PayloadAction<string>) => {
      state.lastRefreshedAt = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Stats thunk
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsStatus = 'loading';
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.statsStatus = 'succeeded';
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.statsStatus = 'failed';
        state.statsError = action.payload ?? 'Unknown error';
      });

    // Recent orders thunk
    builder
      .addCase(fetchRecentOrders.pending, (state) => {
        state.ordersStatus = 'loading';
        state.ordersError = null;
      })
      .addCase(fetchRecentOrders.fulfilled, (state, action) => {
        state.ordersStatus = 'succeeded';
        state.recentOrders = action.payload;
      })
      .addCase(fetchRecentOrders.rejected, (state, action) => {
        state.ordersStatus = 'failed';
        state.ordersError = action.payload ?? 'Unknown error';
      });
  },
});

export const { resetDashboard, setLastRefreshedAt } = adminDashboardSlice.actions;
export const adminDashboardReducer = adminDashboardSlice.reducer;

// ---------------------------------------------------------------------------
// Typed selectors
// ---------------------------------------------------------------------------

export interface RootStateWithDashboard {
  adminDashboard: AdminDashboardState;
}

export const selectDashboardStats = (state: RootStateWithDashboard) =>
  state.adminDashboard.stats;

export const selectDashboardStatsStatus = (state: RootStateWithDashboard) =>
  state.adminDashboard.statsStatus;

export const selectDashboardStatsError = (state: RootStateWithDashboard) =>
  state.adminDashboard.statsError;

export const selectRecentOrders = (state: RootStateWithDashboard) =>
  state.adminDashboard.recentOrders;

export const selectRecentOrdersStatus = (state: RootStateWithDashboard) =>
  state.adminDashboard.ordersStatus;

export const selectRecentOrdersError = (state: RootStateWithDashboard) =>
  state.adminDashboard.ordersError;

export const selectLastRefreshedAt = (state: RootStateWithDashboard) =>
  state.adminDashboard.lastRefreshedAt;
